import { useState, useEffect, useCallback, useRef } from 'react';
import axiosInstance from '../../../Config/axiosInstance';
import { Container, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, MenuItem, Button, Grid,
  Typography, CircularProgress, Alert, Box, Menu, ListItemIcon, ListItemText, OutlinedInput, Select, Checkbox, FormControl, 
  InputLabel} from '@mui/material';
import { Save, PictureAsPdf, GetApp, Assessment } from '@mui/icons-material';
import './ExpensesReports.css';
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import ExcelJS from 'exceljs';
import {saveAs} from "file-saver";
import logoImg from '../../../Images/logo_pmpc.png'

const ExpenseReport = () => {
  const [filters, setFilters] = useState({
    month: (new Date().getMonth() + 1).toString().padStart(2, '0'),
    year: new Date().getFullYear(),
    expenseTypes: []
  });
  
  const [expenseTypeOptions, setExpenseTypeOptions] = useState([]);
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [error, setError] = useState('');
  const [reportGenerated, setReportGenerated] = useState(false); 

  const monthOptions = [
    { value: '01', label: 'January' }, { value: '02', label: 'February' },
    { value: '03', label: 'March' }, { value: '04', label: 'April' },
    { value: '05', label: 'May' }, { value: '06', label: 'June' },
    { value: '07', label: 'July' }, { value: '08', label: 'August' },
    { value: '09', label: 'September' }, { value: '10', label: 'October' },
    { value: '11', label: 'November' }, { value: '12', label: 'December' },
  ];

  const currentYear = new Date().getFullYear();
  const yearOptions = Array.from({ length: 8 }, (_, i) => currentYear - 2 + i);

  useEffect(() => {
    fetchExpenseTypes();
  }, []);

  const fetchExpenseTypes = async () => {
    setLoadingTypes(true);
    try {
      const response = await axiosInstance.get('/expenses-type/');
      setExpenseTypeOptions(response.data);
      const allTypeIds = response.data.map(type => type.id);
      setFilters(prev => ({ ...prev, expenseTypes: allTypeIds }));
    } catch (err) {
      console.error('Error fetching expense types:', err);
      setError('Error loading the expense types.');
    } finally {
      setLoadingTypes(false);
    }
  };

  const fetchReport = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {
        month: filters.month.toString().padStart(2, '0'),
        year: filters.year
      };
      
      if (filters.expenseTypes && filters.expenseTypes.length > 0) {
        params.expense_types = filters.expenseTypes.join(',');
      }
      
      const response = await axiosInstance.get('/reports-expenses/', { params });
      setReportData(response.data);
      setReportGenerated(true); 
    } catch (err) {
      console.error('Error fetching report:', err);
      setError('Error loading the report. Check the parameters.');
      setReportData(null);
      setReportGenerated(false);
    } finally {
      setLoading(false);
    }
  }, [filters.month, filters.year, filters.expenseTypes]);


  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    setReportGenerated(false);
  };

  const handleExpenseTypeChange = (event) => {
    const {
      target: { value },
    } = event;
    
    if (value[value.length - 1] === 'All') {
      if (filters.expenseTypes.length === expenseTypeOptions.length) {
        setFilters(prev => ({ ...prev, expenseTypes: [] }));
      } else {
        const allTypeIds = expenseTypeOptions.map(type => type.id);
        setFilters(prev => ({ ...prev, expenseTypes: allTypeIds }));
      }
    } else {
      setFilters(prev => ({ ...prev, expenseTypes: value.filter(item => item !== 'All') }));
    }

    setReportGenerated(false);
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    fetchReport();
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  }

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return new Intl.NumberFormat('en-EN', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const calculateDifference = (target, actualCost) => {
    if (target === null || actualCost === null) return 0;
    return target - actualCost;
  };

  const renderSelectedExpenseTypes = (selected) => {
    if (selected.length === 0) {
      return 'None selected';
    }
    if (selected.length === expenseTypeOptions.length) {
      return 'All';
    }
    return `${selected.length} selected`;
  };

  const reportRef = useRef();
// -------------------------------------
  //  PDF EXPORT FUNCTION WITH LOGO
  // -------------------------------------
  const exportPDF = async () => {

    // PDF portrait
    const pdf = new jsPDF("p", "mm", "a4");
    const pdfWidth = pdf.internal.pageSize.getWidth();
    const pdfHeight = pdf.internal.pageSize.getHeight();

    // --------------------------------------------------
    // HEADER: Logo + bare decorative
    // --------------------------------------------------
    const logo = "/logo_pmpc.png";

    // Logo
    pdf.addImage(logo, "PNG", 8, 3, 28, 8);

    // Dimensiuni elemente decorative
    const triangleWidth = 20;
    const totalWidth = pdfWidth * 0.25;
    const barWidth = totalWidth - triangleWidth;

    const barHeight = 1.2;     // bara scurtă + triunghi
    const secondBarHeight = 2; // bara lungă
    const barTop = 15;
    const barBottom = barTop + barHeight;

    // Bara scurtă
    pdf.setFillColor(23, 52, 88);
    const barOverlap = 1; // cât intră bara peste triunghi

    pdf.rect(0, barTop, barWidth + barOverlap, barHeight, "F");


    // Triunghi (colț în jos)
    pdf.triangle(
      barWidth, barBottom,                   // colț stânga jos
      barWidth + triangleWidth, barBottom,   // colț dreapta jos
      barWidth, barTop,                      // vârful sus
      "F"
    );

    // SUPRAPUNERE — linia lungă intră peste triunghi
    const overlap = 0.6;
    const headerLongBarTop = barBottom - overlap;

    // Bara lungă full-width (header)
    pdf.rect(0, headerLongBarTop, pdfWidth, secondBarHeight, "F");

    const dottedLineY = headerLongBarTop + secondBarHeight;


    // --------------------------------------------------
    // TITLU
    // --------------------------------------------------
    const selectedMonthName = monthOptions.find(m => m.value === filters.month)?.label || "";
    const reportTitle = `Expenses Report - ${selectedMonthName} ${filters.year}`;

    pdf.setFontSize(14);
    pdf.setFont("helvetica", "bold");
    pdf.text(reportTitle, pdfWidth / 2, dottedLineY + 12, { align: "center" });

    const contentTop = dottedLineY + 20;
    // --------------------------------------------------
    // MAIN CONTENT — TABEL REAL PDF
    // --------------------------------------------------

    if (reportData?.summary_by_expense_type?.length > 0) {

      const tableHeaders = [
        [
          "Expense Type",
          "Actual Cost Without VAT",
          "Actual Paid Without VAT",
          "Actual Cost With VAT",
          "Actual Paid With VAT",
          "Target",
          "Target - Actual Cost Without VAT"
        ]
      ];

      const tableData = reportData.summary_by_expense_type.map(row => {
        const diff = row.target - row.total_without_vat;

        return [
          row.expenses_type_name,
          formatNumber(row.total_without_vat),
          formatNumber(row.actual_paid_without_vat),
          formatNumber(row.total_with_vat),
          formatNumber(row.actual_paid_with_vat),
          formatNumber(row.target),
          diff >= 0 ? `+${formatNumber(diff)}` : formatNumber(diff),
        ];
      });

      autoTable(pdf, {
        startY: contentTop,
        head: tableHeaders,
        body: tableData,

        styles: {
          fontSize: 8,
          cellPadding: 2,
          overflow: "linebreak",
          lineColor: [180, 180, 180],
          lineWidth: 0.25,
          valign: "middle",
        },

        headStyles: {
          fillColor: [232, 232, 232],
          textColor: 0,
          fontStyle: "bold",
          halign: "center",
        },

        columnStyles: {
          1: { halign: "right" },
          2: { halign: "right" },
          3: { halign: "right" },
          4: { halign: "right" },
          5: { halign: "right" },
          6: {
            halign: "right",
            fontStyle: "bold"
          }
        },

        alternateRowStyles: {
          fillColor: [245, 247, 250]
        },

        margin: { left: 5, right: 5 },

        didParseCell: data => {
          if (data.section === "body" && data.column.index === 6) {
            // colorăm coloana Target - Actual
            const value = parseFloat(data.cell.raw.replace(",", ""));
            data.cell.styles.textColor = value >= 0 ? [0, 130, 0] : [200, 0, 0];
          }
        }
      });
    }

    // ------------------------------
    // BARE DECORATIVE JOS — oglindire
    // ------------------------------

    pdf.setFillColor(23, 52, 88);

    const bottomOffset = 12; // distanță față de marginea de jos
    const longBarTop = pdfHeight - bottomOffset; // poziția unde începe bara lungă

    // SUPRAPUNERE — bara lungă intră peste triunghi (oglindit)
    const verticalOverlapFooter = 0.6;

    // Bara lungă jos (full width)
    pdf.rect(
      0,
      longBarTop - verticalOverlapFooter + 0.1,
      pdfWidth,
      secondBarHeight,
      "F"
    );

    // Bara scurtă + triunghi (oglindite)
    const shortBarTop = longBarTop + secondBarHeight - verticalOverlapFooter;
    const shortBarBottom = shortBarTop + barHeight;

    // Bara scurtă (identic cu header-ul)
    const barOverlapFooter = 1;
    pdf.rect(
      0,
      shortBarTop,
      barWidth + barOverlapFooter,
      barHeight,
      "F"
    );

    // Triunghiul cu colțul ÎN JOS (oglindit față de header)
    pdf.triangle(
      barWidth, shortBarTop,                         // colț stânga sus
      barWidth + triangleWidth, shortBarTop,         // colț dreapta sus
      barWidth, shortBarBottom,                      // vârful jos
      "F"
    );

    // --------------------------------------------------
    // FOOTER
    // --------------------------------------------------
    pdf.setFontSize(7);
    pdf.setFont("helvetica", "normal");
    pdf.text("Generated by PMPC ERP", 10, pdfHeight - 3);
    const today = new Date();
    const currentDate = today.toLocaleDateString("ro-RO"); // ex. 11.12.2025

    pdf.text(`${currentDate} `, pdfWidth - 30, pdfHeight - 3);



    // SAVE PDF
    pdf.save(`ExpensesReport_${filters.month}_${filters.year}.pdf`);
  };

  const exportToExcel = async (data = null, customTitle = null) => {
    const exportData = data || reportData?.summary_by_expense_type;
    const title = customTitle || `Expenses Report - ${monthOptions.find(m => m.value === filters.month)?.label} ${filters.year}`;
    
    if (!exportData?.length) {
        alert("No data to export!");
        return;
    }

    const workbook = new ExcelJS.Workbook();
    const sheet = workbook.addWorksheet('Report');

    //logo
    try {
        sheet.mergeCells('A1:A4');
        const logo = workbook.addImage({
            buffer: await fetch(logoImg).then(res => res.arrayBuffer()),
            extension: 'png',
        });
        sheet.addImage(logo, { tl: { col: 0, row: 0 }, br: { col: 1, row: 4 } });
    } catch (error) {
        console.warn('Logo could not be loaded:', error);
    }

    //titlu
    sheet.mergeCells('B1:G4');
    const titleCell = sheet.getCell('B1');
    titleCell.value = title;
    titleCell.font = { size: 14, bold: true };
    titleCell.alignment = { 
        vertical: 'middle', 
        horizontal: 'center', 
        wrapText: true 
    };

    //rand liber dupa titlu
    sheet.insertRow(5, []); 
    
    //header
    const headerRowNumber = 6;
    const headers = ["Expense Type", "Actual Cost Without VAT", "Actual Paid Without VAT", 
                    "Actual Cost With VAT", "Actual Paid With VAT", "Target", 
                    "Target - Actual Cost Without VAT"];
    
    const headerRow = sheet.getRow(headerRowNumber);
    headerRow.values = headers;
    //stil pentru header
    headerRow.font = { bold: true };
    headerRow.eachCell((cell) => {
        cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: true };
        cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE8E8E8' } };
    });

    // se adauga randul cu date de la linia 7
    exportData.forEach((row, index) => {
        const dataRowNumber = headerRowNumber + 1 + index;
        const difference = row.target - row.total_without_vat;
      
        sheet.insertRow(dataRowNumber, [
            row.expenses_type_name,
            row.total_without_vat || 0,
            row.actual_paid_without_vat || 0,
            row.total_with_vat || 0,
            row.actual_paid_with_vat || 0,
            row.target || 0,
            difference || 0
        ]);
        
        const dataRow = sheet.getRow(dataRowNumber);
        
        //formatare cu 2 zecimale pentru toate coloanele numerice (coloanele 2-7)
        for (let col = 2; col <= 7; col++) {
            const cell = dataRow.getCell(col);
            cell.numFmt = '0.00';
        }
        dataRow.getCell(1).alignment = { wrapText: true };
        //culoare pentru ultima coloana
        const diffCell = dataRow.getCell(7);
        diffCell.font = { 
            color: { argb: difference >= 0 ? 'FF008000' : 'FFFF0000' },
            bold: true 
        };
    });

    //auto-width
    sheet.columns.forEach((column, columnIndex) => {
        let maxLength = 0;
        column.eachCell({ includeEmpty: true }, (cell) => {
            let cellValue = cell.value?.toString() || '';
            if (cell.numFmt === '0.00' && !isNaN(cell.value)) {
                cellValue = parseFloat(cell.value || 0).toFixed(2);
            }
            maxLength = Math.max(maxLength, cellValue.length);
        });
        
        //factor 0.85 pentru prima coloana, restul: factor 0.75
        if (columnIndex === 0) {
            column.width = Math.max(maxLength * 0.85, 15);
        } else {
            column.width = Math.max(maxLength * 0.75, 12);
        }
    });

    //salvare
    const buffer = await workbook.xlsx.writeBuffer();
    saveAs(new Blob([buffer]), `ExpensesReport_${filters.month}_${filters.year}.xlsx`);
  };


  return (
    <Container maxWidth={false} disableGutters className="exp-reports expenses-reports">
      <Container maxWidth={false} disableGutters className="expense-container">
        <Typography variant="h4" gutterBottom marginTop="20px">
          Expenses Report
        </Typography>
        
      <Paper className="filters-paper">
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={9}>
              <Box display="flex" flexWrap="wrap" alignItems="center" gap={1}>
                <TextField
                  select
                  label="Month"
                  value={filters.month}
                  onChange={(e) => handleFilterChange('month', e.target.value)}
                  required
                  size="small"
                  className="month-select"
                >
                  {monthOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </TextField>

                <TextField
                  select
                  label="Year"
                  value={filters.year}
                  onChange={(e) => handleFilterChange('year', e.target.value)}
                  required
                  size="small"
                  className="year-select"
                >
                  {yearOptions.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </TextField>

                <FormControl size="small" className="expense-type-form-control" sx={{ minWidth: 250 }}>
                  <InputLabel id="expense-type-label">Expense Type</InputLabel>
                  <Select
                    labelId="expense-type-label"
                    multiple
                    value={filters.expenseTypes}
                    onChange={handleExpenseTypeChange}
                    input={<OutlinedInput label="Expense Type" />}
                    renderValue={renderSelectedExpenseTypes}
                    MenuProps={{
                      PaperProps: {
                        sx: {
                          width: 250,
                          '& .MuiMenuItem-root': {
                            fontSize: '0.75rem',
                            paddingTop: 0.25,
                            paddingBottom: 0.25,
                            whiteSpace: 'normal',
                            wordBreak: 'break-word',
                          },
                          '& .MuiCheckbox-root': {
                            padding: '2px',
                            marginRight: '4px',
                          },
                        },
                      },
                    }}
                  >
                    <MenuItem value="All">
                      <Checkbox
                        size="small"
                        checked={filters.expenseTypes.length === expenseTypeOptions.length}
                        sx={{ padding: '2px', marginRight: '4px' }}
                      />
                      <ListItemText primary="ALL" />
                    </MenuItem>
                    {expenseTypeOptions.map((type) => (
                      <MenuItem key={type.id} value={type.id}>
                        <Checkbox
                          size="small"
                          checked={filters.expenseTypes.includes(type.id)}
                          sx={{ padding: '2px', marginRight: '4px' }}
                        />
                        <ListItemText primary={type.type || type.name} />
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

                <Button
                  type="submit"
                  variant="contained"
                  color="primary"
                  size="small"
                  className="generate-button"
                  startIcon={loading ? <CircularProgress size={20} /> : <Assessment />}
                  disabled={loadingTypes || loading}
                >
                  Generate Report
                </Button>
              </Box>
            </Grid>

            <Grid item xs={12} md={3}>
              <Grid container spacing={1} justifyContent={{ xs: 'flex-start', md: 'flex-end' }} wrap="wrap">
                <Grid item>
                  <Button
                    variant="contained"
                    size="small"
                    onClick={handleClick}
                    className="save-button"
                    startIcon={<Save />}
                    disabled={!reportGenerated}
                  >
                    Save As
                  </Button>
                </Grid>

                <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={handleClose}>
                  <MenuItem onClick={() => { handleClose(); exportPDF(); }}>
                    <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
                    <ListItemText>Save as PDF</ListItemText>
                  </MenuItem>

                  <MenuItem onClick={() => { handleClose(); exportToExcel(); }}>
                    <ListItemIcon><GetApp fontSize="small" /></ListItemIcon>
                    <ListItemText>Save as Excel</ListItemText>
                  </MenuItem>
                </Menu>
              </Grid>
            </Grid>

          </Grid>
        </form>
      </Paper>


        {error && (
          <Alert severity="error" className="error-alert">
            {error}
          </Alert>
        )}

        <div ref={reportRef}>
        {reportGenerated && reportData?.summary_by_expense_type && reportData.summary_by_expense_type.length > 0 && (
          <>
            <TableContainer component={Paper} className="report-table-container">
              <Table className="report-table">
                <TableHead>
                  <TableRow className="table-header">
                    <TableCell className="table-header-cell">Expense Type</TableCell>
                    <TableCell align="center" className="table-header-cell">Actual Cost Without VAT</TableCell>
                    <TableCell align="center" className="table-header-cell">Actual Paid Without VAT</TableCell>
                    <TableCell align="center" className="table-header-cell">Actual Cost With VAT</TableCell>
                    <TableCell align="center" className="table-header-cell">Actual Paid With VAT</TableCell>
                    <TableCell align="center" className="table-header-cell">Target</TableCell>
                    <TableCell align="center" className="table-header-cell">Target - Actual Cost Without VAT</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.summary_by_expense_type.map((row) => {
                    const difference = calculateDifference(row.target, row.total_without_vat);
                    return (
                      <TableRow key={row.expenses_type_pk || row.expenses_type_id} className="table-row">
                        <TableCell className="table-cell">{row.expenses_type_name}</TableCell>
                        <TableCell align="center" className="table-cell">{formatNumber(row.total_without_vat)}</TableCell>
                        <TableCell align="center" className="table-cell">{formatNumber(row.actual_paid_without_vat)}</TableCell>
                        <TableCell align="center" className="table-cell">{formatNumber(row.total_with_vat)}</TableCell>
                        <TableCell align="center" className="table-cell">{formatNumber(row.actual_paid_with_vat)}</TableCell>
                        <TableCell align="center" className="table-cell">{formatNumber(row.target)}</TableCell>
                        <TableCell
                          align="center"
                          className={`table-cell difference-cell ${difference >= 0 ? 'positive-difference' : 'negative-difference'}`}
                        >
                          {formatNumber(difference)}
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
        
        {reportGenerated && reportData?.summary_by_expense_type?.length === 0 && !loading && (
          <Alert severity="info" className="info-alert">
            There is no data for the selected filters.
          </Alert>
        )}
        
        {reportGenerated && reportData && (
          <Box className="report-footer">
            <Typography variant="caption" className="footer-text">
              Generated on {reportData.report_date || new Date().toLocaleString('ro-RO')} |{' '}
              {monthOptions.find(m => m.value === filters.month)?.label} {filters.year} |{' '}
              Expense Types: {filters.expenseTypes.length === expenseTypeOptions.length ? 'All' : `${filters.expenseTypes.length} selected`}
            </Typography>
          </Box>
        )}
        </div>
      </Container>
    </Container>
  );
};

export default ExpenseReport;